import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      return [];
    }
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    calculateTotal(cart);
  }, [cart]);

  const calculateTotal = (cartItems) => {
    const newTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    setTotal(newTotal);
  };

  const saveCart = (newCart) => {
    try {
      localStorage.setItem('cart', JSON.stringify(newCart));
      setCart(newCart);
    } catch (error) {
      console.error('Error saving cart:', error);
      throw error;
    }
  };

  const addToCart = (item) => {
    console.log('Adding item to cart:', item);
    try {
      const existingItemIndex = cart.findIndex(i => i.id === item.id);
      let newCart;

      if (existingItemIndex >= 0) {
        // Update quantity if item exists
        newCart = cart.map((cartItem, index) => {
          if (index === existingItemIndex) {
            return { ...cartItem, quantity: cartItem.quantity + 1 };
          }
          return cartItem;
        });
      } else {
        // Add new item
        newCart = [...cart, { ...item, quantity: 1 }];
      }

      saveCart(newCart);
      console.log('Updated cart:', newCart);
      return Promise.resolve();
    } catch (error) {
      console.error('Error adding to cart:', error);
      return Promise.reject(error);
    }
  };

  const removeFromCart = (itemId) => {
    try {
      const newCart = cart.filter(item => item.id !== itemId);
      saveCart(newCart);
      return Promise.resolve();
    } catch (error) {
      console.error('Error removing from cart:', error);
      return Promise.reject(error);
    }
  };

  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) return Promise.resolve();

    try {
      const newCart = cart.map(item => {
        if (item.id === itemId) {
          return { ...item, quantity };
        }
        return item;
      });

      saveCart(newCart);
      return Promise.resolve();
    } catch (error) {
      console.error('Error updating quantity:', error);
      return Promise.reject(error);
    }
  };

  const clearCart = () => {
    try {
      localStorage.removeItem('cart');
      setCart([]);
      setTotal(0);
      return Promise.resolve();
    } catch (error) {
      console.error('Error clearing cart:', error);
      return Promise.reject(error);
    }
  };

  const value = {
    cart,
    total,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
